import StoreConnector from '@/helpers/StoreConnector'
import DealerAccessories from 'api-client/DealerAccessory.api'
import QuoteApi from 'api-client/Quote.api'
import ConfigurationApi from 'api-client/Configuration'
import FleetSalesContracts from 'api-client/FleetSalesContracts'
import DeliveryDatesApi from 'api-client/DeliveryDates.api'
import moment from 'moment'
import { EventBus } from '@/plugins/event-bus'
import loadAccessoriesAction from '@/helpers/enums/loadAccessoriesAction'
import Store from '@/store/store'
import digitalSigningStatus from '@/helpers/enums/digitalSigningStatus'

async function fetchAccessoriesOrReportFailure(fetchAccessories) {
  try {
    return await fetchAccessories()
  } catch {
    StoreConnector.commit('accessories/setLoadFailed', true)
    EventBus.$emit('notification-warning', 'Fout bij het laden van de accessoires')
    return undefined
  }
}

const QuoteOrderService = {
  type: 'quote',
  addControlCode(selectedProduct) {
    StoreConnector.dispatch('quote/addControlCode', selectedProduct)
  },
  removeControlCode(deselectedProduct) {
    StoreConnector.dispatch('quote/removeFromControlCodes', deselectedProduct)
  },
  addOrUpdateCustomer(customer) {
    StoreConnector.dispatch('quote/setCustomer', customer)
    StoreConnector.dispatch('quote/createOrUpdate', customer)
  },
  async duplicateQuote() {
    const getters = window.VUE_CLI_EXPORTS.plugins.Store.getters

    const quoteToDuplicate = getters['quote/get']

    // PCQM stamps the duplicate with whoever created it, and only ever sees a
    // client-credentials token, so the employee has to travel in the body.
    const employee = {
      PonId: getters['user/userName'],
      AutolineId: getters['user/autolineId'],
      EmployeeName: getters['user/name'],
      EmployeeEmail: getters['user/email'],
    }

    let resp = await QuoteApi.duplicate(quoteToDuplicate.QuoteId, employee)

    if (resp.data?.QuoteId) {
      // If there was a FSC present, update this to the newly created quote
      const hasFleetSalesContract = getters['quote/hasFleetSalesContract']
      if (hasFleetSalesContract) {
        const now = moment.utc().format()
        const contractNumber = quoteToDuplicate.QuoteDetails?.FleetSalesContract?.ContractNumber

        await FleetSalesContracts.setContract(now, contractNumber, resp.data?.QuoteId)
      }
    }

    return resp
  },
  async save(options) {
    const currentQuote = window.VUE_CLI_EXPORTS.plugins.Store.getters['quote/get']

    let resp = await QuoteApi.update(currentQuote, options)
    return resp
  },
  async createOrUpdate(options) {},
  updateChildQuoteStatus(childQuoteId, status) {
    Store.commit('quote/updateChildQuoteStatus', {
      id: childQuoteId,
      status: status,
    })
  },
  async requestSalesOrder(salesOrderParams) {
    let quoteHasErrors = false
    let quoteErrors = []
    let reservedInIds = false
    let idsStatusMessage = ''
    let requestingSalesOrderErrors = []
    let salesOrderHasErrors = false
    let { quoteId, sendSigning, digitalSigningParams } = salesOrderParams

    try {
      let resp = await QuoteApi.requestSalesOrder(salesOrderParams)

      reservedInIds = resp?.data?.reservationStatusIds
      idsStatusMessage = resp?.data?.reservationStatusIds?.StatusMessage
    } catch (e) {
      salesOrderHasErrors = true
      requestingSalesOrderErrors = e.response.data.Messages
    }

    try {
      // get the quote to check if it was succesfully ordered
      const quote = await QuoteApi.getQuoteById(quoteId)

      if (quote.ParentId) {
        quoteHasErrors = false
        await StoreConnector.dispatch('additionalQuote/setQuote', quote)
        this.updateChildQuoteStatus(quoteId, quote.Status)
      } else {
        quoteHasErrors = !sendSigning && quote.SalesOrderSendToAutoline === false
        quoteErrors = quote.Errors?.Messages
        await StoreConnector.dispatch('quote/setQuote', quote)
        if (sendSigning && digitalSigningParams && !salesOrderHasErrors) {
          Store.commit('quote/setSigningStatus', digitalSigningStatus.sentToCustomer)
        }
      }
    } catch (e) {
      quoteErrors = e.response.data.Messages
    }

    return {
      salesOrderHasErrors,
      quoteErrors,
      quoteHasErrors,
      reservedInIds,
      idsStatusMessage,
      requestingSalesOrderErrors,
    }
  },
  async sendOffer(quoteId, sendEmailToCustomer, sendCopyToEmployee, reserveInIds = false) {
    let quoteHasErrors = false
    let quoteErrors = []
    let reservedInIds = false
    let idsStatusMessage = ''
    let requestingQuoteErrors = []
    let requestQuoteHasErrors = false

    try {
      let resp = await QuoteApi.requestQuote(
        quoteId,
        sendEmailToCustomer,
        sendCopyToEmployee,
        reserveInIds
      )
      reservedInIds = resp?.data?.reservationStatusIds
      idsStatusMessage = resp?.data?.reservationStatusIds?.StatusMessage
    } catch (e) {
      requestQuoteHasErrors = true
      requestingQuoteErrors = e.response.data.Messages
    }

    try {
      const quote = await QuoteApi.getQuoteById(quoteId)
      quoteHasErrors = quote.QuoteSendToAutoline === false
      quoteErrors = quote.Errors?.Messages
      if (quote.ParentId) {
        await StoreConnector.dispatch('additionalQuote/setQuote', quote)
        this.updateChildQuoteStatus(quoteId, quote.Status)
      } else {
        await StoreConnector.dispatch('quote/setQuote', quote)
      }
    } catch (e) {
      quoteErrors = e.response.data.Messages
    }

    return {
      requestQuoteHasErrors,
      quoteErrors,
      quoteHasErrors,
      reservedInIds,
      idsStatusMessage,
      requestingQuoteErrors,
    }
  },
  async updateConfigCode(configCode, emitEvent = true) {
    const deliveryDate = window.VUE_CLI_EXPORTS.plugins.Store.getters['quote/deliveryDate']
    const bpmCalculated = window.VUE_CLI_EXPORTS.plugins.Store.getters['quote/bpmCalculated']
    return this.updateConfiguration(
      configCode,
      deliveryDate,
      loadAccessoriesAction.renew,
      emitEvent,
      bpmCalculated
    )
  },
  async updateDeliveryDate(deliveryDate) {
    const car = window.VUE_CLI_EXPORTS.plugins.Store.getters['quote/car']
    const bpmCalculated = window.VUE_CLI_EXPORTS.plugins.Store.getters['quote/bpmCalculated']
    const priceBefore = this.getTotalPriceExclBtwInclBpm()

    if (car.Type === 'STOCKCAR') {
      await this.updateStockCar(car.StockCarId, deliveryDate, bpmCalculated)
    }
    if (car.Type === 'STOCKCAR_V2') {
      await this.updateStock(car.StockId, deliveryDate, bpmCalculated)
    }
    if (car.Type === 'CONFIGURATION') {
      await this.updateConfiguration(
        car.ConfigurationCode,
        deliveryDate,
        loadAccessoriesAction.keep,
        null,
        false,
        bpmCalculated
      )
    }
    if (this.getTotalPriceExclBtwInclBpm() !== priceBefore) {
      window.VUE_CLI_EXPORTS.plugins.EventBus.$emit('quote/deliveryDateUpdated')
    }
  },
  getTotalPriceExclBtwInclBpm() {
    return window.VUE_CLI_EXPORTS.plugins.Store.getters['quote/totalPrice']?.ExclBTWinclBPM
  },
  async updateCalculateBpm(calculateBpm) {
    const car = window.VUE_CLI_EXPORTS.plugins.Store.getters['quote/car']
    const deliveryDate = window.VUE_CLI_EXPORTS.plugins.Store.getters['quote/deliveryDate']

    if (car.Type === 'STOCKCAR') {
      StoreConnector.dispatch('configuration/getStockcar', {
        stockCarId: car.StockCarId,
        configurationId: null,
        type: 'STOCKCAR',
        deliveryDate: deliveryDate,
        calculateBpm: calculateBpm,
      }).then(() => {
        StoreConnector.commit('quote/setCalculateBpm', calculateBpm)
        StoreConnector.dispatch('quote/previewSave')
      })
    }

    if (car.Type === 'STOCKCAR_V2') {
      await StoreConnector.dispatch('configuration/getNewStock', {
        stockId: car.StockId,
        configurationId: car.ConfigurationId,
        type: 'STOCKCAR_V2',
        deliveryDate: deliveryDate,
        calculateBpm: calculateBpm,
      }).then(() => {
        StoreConnector.commit('quote/setCalculateBpm', calculateBpm)
        StoreConnector.dispatch('quote/previewSave')
      })
    }

    if (car.Type === 'CONFIGURATION') {
      StoreConnector.dispatch('configuration/getConfiguration', {
        configurationCode: car.ConfigurationCode,
        deliveryDate,
        calculateBpm,
      }).then(() => {
        StoreConnector.commit('quote/setCalculateBpm', calculateBpm)
        StoreConnector.dispatch('quote/previewSave')
      })
    }
  },
  /** Called when changing the configurationCode or deliveryDate
   * @param {loadAccessoriesAction} loadAccessories
   * keep: Keep valid accessories and prices
   * renew: Get new accessories matching those on quote with new prices
   * overwrite: Remove all existing accessories and load standard accessories
   */
  async updateConfiguration(
    configurationCode,
    deliveryDate,
    loadAccessories,
    configuration = null,
    emitEvent = false,
    calculateBpm = null
  ) {
    if (emitEvent) {
      StoreConnector.commit('configuration/setIsUpdating', true)
    }
    if (configuration) {
      // Already retrieved config, only need to set in store
      await StoreConnector.dispatch('configuration/setAfterGetConfiguration', {
        configurationCode,
        response: configuration,
      })
    } else {
      configuration = await StoreConnector.dispatch('configuration/getConfiguration', {
        configurationCode,
        deliveryDate,
        calculateBpm,
      })
    }
    if (configuration == undefined) return

    await StoreConnector.dispatch('quote/setConfigurationCode', configurationCode)
    await StoreConnector.dispatch('quote/setDeliveryDate', deliveryDate)

    const accessories = await fetchAccessoriesOrReportFailure(() =>
      DealerAccessories.getForConfigurationCode(configurationCode)
    )

    await StoreConnector.dispatch('accessories/updateAccessories', {
      accessories,
      checkPrices: true,
      loadAccessories,
      configurationCode:
        loadAccessories === loadAccessoriesAction.overwrite ? configurationCode : null,
    })

    await StoreConnector.dispatch('controlCodes/getQuoteOrderControlCodesForConfigurationCode', {
      configurationCode,
      deliveryDate,
    })

    await StoreConnector.dispatch('quote/previewSave')

    if (emitEvent) {
      window.VUE_CLI_EXPORTS.plugins.EventBus.$emit('configuration-updated')
      StoreConnector.commit('configuration/setIsUpdating', false)
    }
  },
  async updateStockCar(stockCarId, deliveryDate, calculateBpm = null) {
    const stockcar = await StoreConnector.dispatch('configuration/getStockcar', {
      stockCarId: stockCarId,
      configurationId: null,
      type: 'STOCKCAR',
      deliveryDate: deliveryDate,
      calculateBpm: calculateBpm,
    })

    if (stockcar == undefined) return

    const stockCarPayload = {
      Type: 'STOCKCAR',
      KomNumber: {
        Number: stockcar.StockCarDetails.KomNumber.Number,
        Year: stockcar.StockCarDetails.KomNumber.Year,
        Date: stockcar.StockCarDetails.KomNumber.Date,
      },
      ConfigurationCode: null,
      StockCarId: stockcar.Id,
    }

    await StoreConnector.dispatch('quote/setStockCarId', stockCarId)
    await StoreConnector.dispatch('quote/setDeliveryDate', deliveryDate)
    await StoreConnector.dispatch('quote/setCar', stockCarPayload)

    const accessories = await fetchAccessoriesOrReportFailure(() =>
      DealerAccessories.getForStockCarId(stockCarId)
    )

    await StoreConnector.dispatch('accessories/updateAccessories', {
      accessories,
      checkPrices: true,
      loadAccessories: loadAccessoriesAction.keep,
    })

    await StoreConnector.dispatch('controlCodes/getQuoteOrderControlCodesForStockCar', {
      stockCarId,
      deliveryDate,
    })

    await StoreConnector.dispatch('quote/previewSave')
  },
  async updateStock(stockId, deliveryDate, calculateBpm) {
    var stock = await StoreConnector.dispatch('configuration/getNewStock', {
      stockId: stockId,
      deliveryDate: deliveryDate,
      calculateBpm: calculateBpm,
    })

    if (stock === undefined) return

    const stockCarPayload = {
      Type: 'STOCKCAR_V2',
      KomNumber: {
        Number: stock.CommissionNumber,
        Year: stock.CommissionYear,
      },
      ConfigurationCode: null,
      StockId: stockId,
    }

    await StoreConnector.dispatch('quote/setStockId', stockId)
    await StoreConnector.dispatch('quote/setDeliveryDate', deliveryDate)
    await StoreConnector.dispatch('quote/setCar', stockCarPayload)

    const accessories = await fetchAccessoriesOrReportFailure(() =>
      DealerAccessories.getForStockId(stockId)
    )

    await StoreConnector.dispatch('accessories/updateAccessories', {
      accessories,
      checkPrices: true,
      loadAccessories: loadAccessoriesAction.keep,
    })

    await StoreConnector.dispatch('controlCodes/getQuoteOrderControlCodesForStock', {
      stockId,
      deliveryDate,
    })

    await StoreConnector.dispatch('quote/previewSave')
  },

  async initConfigurationCarQuote(configurationCode) {
    const dealerSettings = await StoreConnector.getOnce('user/dealerSettings')
    const deliveryDates = dealerSettings.showDeliveryDates
      ? await DeliveryDatesApi.GetDeliveryDatesByConfigurationCode(configurationCode, false)
      : null
    const deliveryDate =
      deliveryDates != null
        ? moment.utc(deliveryDates.ExpectedDeliveryDateCustomer).format().replace('Z', '')
        : moment.utc().format().replace('Z', '')

    let quoteCar = {
      Type: 'CONFIGURATION',
      KomNumber: {
        Number: null,
        Year: null,
        Date: null,
      },
      ConfigurationCode: configurationCode,
      StockCarId: null,
    }

    // new quote with configuration code
    await StoreConnector.dispatch('configuration/getConfiguration', {
      configurationCode,
      deliveryDate,
    })

    await StoreConnector.dispatch('accessories/getForConfigurationCode', {
      configurationCode,
    })

    await StoreConnector.dispatch('controlCodes/initQuoteOrderControlCodesForConfigurationCode', {
      configurationCode,
      deliveryDate,
    })

    await StoreConnector.dispatch('quote/setDeliveryDate', deliveryDate)

    await StoreConnector.dispatch('quote/setCar', quoteCar)
    await StoreConnector.dispatch(
      'quote/setDefaultVwpfsInsurancePdf',
      dealerSettings.vwpfsInsuranceStandaardOn
    )
  },
  async initShoppingCartQuote(cartId) {
    const dealerSettings = await StoreConnector.getOnce('user/dealerSettings')

    var shoppingCart = await ConfigurationApi.getShoppingCart(cartId)
    if (!!shoppingCart) {
      var configurationCode = shoppingCart.Payload.cart.configurationCode

      const deliveryDates = dealerSettings.showDeliveryDates
        ? await DeliveryDatesApi.GetDeliveryDatesByConfigurationCode(configurationCode, false)
        : null
      const deliveryDate =
        deliveryDates != null
          ? moment.utc(deliveryDates.ExpectedDeliveryDateCustomer).format().replace('Z', '')
          : moment.utc().format()

      await StoreConnector.dispatch('quote/setDeliveryDate', deliveryDate)
    } else {
      let errorMessage = `Fout bij het laden van de winkelwagen: ${cartId}`

      EventBus.$emit('notification-warning', errorMessage)
      return Promise.reject(errorMessage)
    }

    let quoteCar = {
      Type: 'CONFIGURATION',
      KomNumber: {
        Number: null,
        Year: null,
        Date: null,
      },
      ConfigurationCode: null,
      StockCarId: null,
    }

    await StoreConnector.dispatch('quote/setCar', quoteCar)
    await StoreConnector.dispatch(
      'quote/setDefaultVwpfsInsurancePdf',
      dealerSettings.vwpfsInsuranceStandaardOn
    )
  },
  async initStockCarQuote(stockCarId) {
    const stockCar = await ConfigurationApi.getStockcar(stockCarId)

    const dealerSettings = await StoreConnector.getOnce('user/dealerSettings')
    const deliveryDates = dealerSettings.showDeliveryDates
      ? await DeliveryDatesApi.GetDeliveryDatesByStockCarId(stockCarId)
      : null
    const deliveryDate =
      deliveryDates != null ? deliveryDates.ExpectedDeliveryDateCustomer : moment.utc().format()

    await StoreConnector.dispatch('configuration/getStockcar', {
      stockCarId: stockCar.Id,
      configurationId: null,
      type: 'STOCKCAR',
      deliveryDate: deliveryDate,
    }).then(async () => {
      StoreConnector.dispatch('quote/setCar', {
        Type: 'STOCKCAR',
        KomNumber: {
          Number: stockCar.StockCarDetails.KomNumber.Number,
          Year: stockCar.StockCarDetails.KomNumber.Year,
          Date: stockCar.StockCarDetails.KomNumber.Date,
        },
        ConfigurationCode: null,
        StockCarId: stockCar.Id,
      })

      await StoreConnector.dispatch('accessories/initForStockcar', {
        stockCarId: stockCar.Id,
      })

      await StoreConnector.dispatch('quote/setDeliveryDate', deliveryDate)

      await StoreConnector.dispatch('controlCodes/initQuoteOrderControlCodesForStockcar', {
        stockCarId: stockCar.Id,
        deliveryDate: deliveryDate,
      })
      await StoreConnector.dispatch(
        'quote/setDefaultVwpfsInsurancePdf',
        dealerSettings.vwpfsInsuranceStandaardOn
      )
    })
  },
  async setNewStockCarDefaultsOnQuote() {
    const dealerSettings = await StoreConnector.getOnce('user/dealerSettings')

    await StoreConnector.dispatch(
      'quote/setDefaultVwpfsInsurancePdf',
      dealerSettings.vwpfsInsuranceStandaardOn
    )
  },
  async getQuotes(establishmentId, page, amount, ponId, status, searchQuery) {
    try {
      const result = await QuoteApi.getQuotes(
        establishmentId,
        page,
        amount,
        ponId,
        status,
        searchQuery
      )
      StoreConnector.dispatch('quotes/addQuotes', result)
      return Promise.resolve()
    } catch (e) {
      EventBus.$emit('notification-warning', 'QuotesGetFailed')
      return Promise.resolve()
    }
  },
  resetQuotes() {
    StoreConnector.dispatch('quotes/reset')
  },
  async deleteQuote(quoteId) {
    try {
      return await QuoteApi.delete(quoteId)
    } catch (e) {
      return Promise.reject(e)
    }
  },
  async changeSalesOrderToQuote(quoteId, ponId) {
    try {
      await QuoteApi.changeStatus(quoteId, 'QUOTE', ponId)
      return Promise.resolve()
    } catch (e) {
      EventBus.$emit('notification-warning', 'QuoteChangeStatusFailed')
      return Promise.reject(e)
    }
  },
  async changeSigningStatus(quoteId, newStatus) {
    try {
      await QuoteApi.changeSigningStatus(quoteId, newStatus)
      return Promise.resolve()
    } catch (e) {
      EventBus.$emit('notification-warning', 'QuoteChangeStatusFailed')
      return Promise.reject(e)
    }
  },
  async transferQuoteEmployee(employeeUserName, employeeName, employeeEmail) {
    // AutolineId too: PCQM overwrites the quote's employee from the body, and C# (which used to
    // fill it from the session on updateEmployee) is no longer in between (CST-4514, F1).
    await StoreConnector.dispatch('quote/setEmployee', {
      EmployeeName: employeeName,
      EmployeeEmail: employeeEmail,
      PonId: employeeUserName,
      AutolineId: StoreConnector.getOnce('user/autolineId'),
    })
    return await this.save({
      shouldUpdateEmployee: true,
      shouldUpdateEstablishment: false,
    })
  },
  async transferQuoteEstablishment(establishmentId) {
    const establishment =
      window.VUE_CLI_EXPORTS.plugins.Store.getters['establishments/establishmentById'](
        establishmentId
      )
    await StoreConnector.dispatch('quote/setEstablishment', establishment)
    await this.save({
      shouldUpdateEmployee: false,
      shouldUpdateEstablishment: true,
    })

    // trigger dealer updated event
    EventBus.$emit('quote/dealer')

    return Promise.resolve()
  },
  async getQuoteEmailInfoForDigitalSigning(quoteId) {
    try {
      return await QuoteApi.getQuoteEmailInfoForDigitalSigning(quoteId)
    } catch (e) {
      EventBus.$emit(
        'notification-warning',
        'Er is een fout opgetreden bij het ophalen de sms-tekst'
      )
      return Promise.reject(e)
    }
  },
}

export default QuoteOrderService
